import { useCallback, useEffect, useMemo } from "react";

import { useSearchParams } from "react-router-dom";

import { BuildingState } from "@/features/cartography/cartographySlice";
import { selectConfigItem } from "@/features/config/configSlice";
import {
  setNavigationFrom,
  setNavigationTo,
} from "@/features/navigation/navigationSlice";

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  handleSetCurrentPoiCategory,
  handleSetCurrentPoiID,
} from "@/features/cartography/actions";
import {
  selectCurrentBuilding,
  selectCurrentBuildingState,
} from "@/features/cartography/cartographySlice.selectors";
import usePOICategoriesOnBuilding from "@/features/cartography/hooks/usePOICategoriesOnBuilding";
import {
  findFlightById,
  selectFlight,
} from "@/features/flight_information/actions";
import type { PoiCategories, Pois } from "@/libs/models";
import { parseQueryParam, type URLProps, updateUrl } from "@/libs/url";

/********************************************************************************
 * POI params
 *********************************************************************************/
const usePoiParams = ({ params, pois }: { params: URLProps; pois: Pois }) => {
  const { poiID, externalPoiId } = params;

  useEffect(() => {
    if (!poiID || !pois?.getPoiById(poiID)) return;

    handleSetCurrentPoiID({ poiId: poiID });
  }, [poiID, pois]);

  // When first loading the map, check for external poi id param. If set,
  // select the poi by external id and delete the param from the url.
  useEffect(() => {
    if (!externalPoiId) return;
    const poi = pois?.findByExternalId(externalPoiId);
    if (!poi) return;

    handleSetCurrentPoiID({ poiId: poi.id });
    updateUrl({ externalPoiId: null });
  }, [externalPoiId, pois]);
};

/********************************************************************************
 * POI category params
 *********************************************************************************/
const usePoiCategoryParams = ({
  params,
  poiCategories,
}: {
  params: URLProps;
  poiCategories: PoiCategories;
}) => {
  const { categoryId, externalCategoryId } = params;

  // When first loading the map, check for category id param. If set,
  // select the category by id and delete the param from the url.
  useEffect(() => {
    if (!categoryId || !poiCategories?.findCategoryById(categoryId)) return;

    handleSetCurrentPoiCategory({ poiCategoryId: categoryId });
    updateUrl({ categoryId: null });
  }, [categoryId, poiCategories]);

  // When first loading the map, check for external category id param. If set,
  // select the category by external id and delete the param from the url.
  useEffect(() => {
    if (!externalCategoryId || !poiCategories) return;

    handleSetCurrentPoiCategory({
      externalPoiCategoryId: externalCategoryId,
    });
    updateUrl({ externalCategoryId: null });
  }, [externalCategoryId, poiCategories]);
};

/********************************************************************************
 * Navigation params
 *********************************************************************************/
const useNavigationParams = ({
  params,
  pois,
}: {
  params: URLProps;
  pois: Pois;
}) => {
  const dispatch = useAppDispatch();
  const { externalNavigationFrom, externalNavigationTo } = params;

  // When first loading the map, check for `external_navigation_from` param. If set,
  // set the navigation "from" to the poi by external id and delete the param from the url.
  useEffect(() => {
    if (!externalNavigationFrom) return;
    const poi = pois?.findByExternalId(externalNavigationFrom);
    if (!poi) return;

    dispatch(setNavigationFrom(poi.id));
    updateUrl({ externalNavigationFrom: null });
  }, [externalNavigationFrom, pois]);

  // When first loading the map, check for `external_navigation_to` param. If set,
  // set the navigation "to" to the poi by external id and delete the param from the url.
  useEffect(() => {
    if (!externalNavigationTo) return;
    const poi = pois?.findByExternalId(externalNavigationTo);
    if (!poi) return;

    dispatch(setNavigationTo(poi.id));
    updateUrl({ externalNavigationTo: null });
  }, [externalNavigationTo, pois]);
};

/********************************************************************************
 * Flight params
 *********************************************************************************/
const useFlightParams = ({
  params,
  isBuildingDataReady,
}: {
  params: URLProps;
  isBuildingDataReady: boolean;
}) => {
  const { flightId, flightNavigationTo, flightNavigationFrom } = params;
  const isFlightModeEnabled = useAppSelector(
    selectConfigItem("flight_information.enabled"),
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isBuildingDataReady || !isFlightModeEnabled) return;
    const flight = findFlightById(flightId);
    if (flight) {
      selectFlight({ flightWithPOI: flight });
      updateUrl({ flightId: null });
    }
  }, [isBuildingDataReady, flightId, isFlightModeEnabled]);

  useEffect(() => {
    if (!isBuildingDataReady || !isFlightModeEnabled) return;
    const flight = findFlightById(flightNavigationTo);
    if (flight?.poiId != null) {
      dispatch(setNavigationTo(flight.poiId));
      updateUrl({ flightNavigationTo: null });
    }
  }, [dispatch, isBuildingDataReady, flightNavigationTo, isFlightModeEnabled]);

  useEffect(() => {
    if (!isBuildingDataReady || !isFlightModeEnabled) return;
    const flight = findFlightById(flightNavigationFrom);
    if (flight?.poiId != null) {
      dispatch(setNavigationFrom(flight.poiId));
      updateUrl({ flightNavigationFrom: null });
    }
  }, [
    dispatch,
    isBuildingDataReady,
    flightNavigationFrom,
    isFlightModeEnabled,
  ]);
};

/********************************************************************************
 * Main hook
 *********************************************************************************/

/**
 * This hook is used to handle query params with identifiers when first
 * loading the map. It selects the poi or category by id or external id
 * and deletes the param from the url (if needed).
 */
export default function useURLIdentifierParams() {
  const [searchParams] = useSearchParams();

  const currentBuilding = useAppSelector(selectCurrentBuilding);
  const currentBuildingState = useAppSelector(selectCurrentBuildingState);
  const poiCategoriesInBuilding = usePOICategoriesOnBuilding();

  const pois = useMemo(() => currentBuilding?.pois, [currentBuilding?.pois]);

  const isBuildingDataReady = useMemo(() => {
    return (
      currentBuildingState === BuildingState.COMPLETE ||
      currentBuildingState === BuildingState.PREFETCHED
    );
  }, [currentBuildingState]);

  const params: URLProps = useMemo(() => {
    return {
      categoryId: parseQueryParam(searchParams, "category_id", "number"),
      externalCategoryId: parseQueryParam(searchParams, "external_category_id"),
      externalNavigationFrom: parseQueryParam(
        searchParams,
        "external_navigation_from",
      ),
      externalNavigationTo: parseQueryParam(
        searchParams,
        "external_navigation_to",
      ),
      externalPoiId: parseQueryParam(searchParams, "external_poi_id"),
      flightId: parseQueryParam(searchParams, "flight_id"),
      flightNavigationFrom: parseQueryParam(
        searchParams,
        "flight_navigation_from",
      ),
      flightNavigationTo: parseQueryParam(searchParams, "flight_navigation_to"),
      poiID: parseQueryParam(searchParams, "poi_id", "number"),
    };
  }, [searchParams]);

  usePoiParams({
    params,
    pois,
  });
  usePoiCategoryParams({
    params,
    poiCategories: poiCategoriesInBuilding,
  });
  useNavigationParams({
    params,
    pois,
  });
  useFlightParams({
    isBuildingDataReady,
    params,
  });
}
